// src/components/resources/glossary/GlossaryCategoryLayout.tsx

'use client'

import { type ReactNode } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import useScroll from '~/lib/use-scroll'
import { Breadcrumbs } from '~/components/resources/Breadcrumbs'
import { GlossaryNavigation } from '~/components/resources/glossary/GlossaryNavigation'

interface GlossaryCategoryLayoutProps {
  children: ReactNode
  title: string
  description: string
  icon: string
}

export function GlossaryCategoryLayout({ children, title, description }: GlossaryCategoryLayoutProps): JSX.Element {
  const scrolled = useScroll(50)
  const showCategoryNav = useScroll(300)

  return (
    <div className="min-h-screen bg-transparent">
      {/* Fixed top navigation bar that extends full width */}
      <div className="fixed top-0 left-0 right-0 z-40">
        {/* Background that extends up */}
        <div 
          className={`absolute inset-0 pointer-events-none transition-all duration-300 ease-in-out ${
            scrolled
              ? 'bg-gradient-to-b from-white via-white to-white/95 dark:from-gray-950 dark:via-gray-950 dark:to-gray-950/95'
              : 'bg-gradient-to-b from-white/60 via-white/30 to-transparent dark:from-gray-950/60 dark:via-gray-950/30 dark:to-transparent'
          }`} 
          style={{
            height: showCategoryNav ? '196px' : '136px',
            transitionProperty: 'background-color, height, opacity'
          }}
        />

        {/* Space for Navbar */}
        <div className="h-[80px]" />
        
        {/* Breadcrumbs Container */}
        <div className="relative">
          <Breadcrumbs />
        </div>

        {/* Glossary Navigation */}
        <AnimatePresence>
          {showCategoryNav && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="relative"
            >
              <GlossaryNavigation />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Main Content */}
      <div className="pt-[160px]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-12">
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
                {title}
              </h1>
              <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
                {description}
              </p>
            </div>
            {children}
          </motion.div>
        </div>
      </div>
    </div>
  )
} 
